import styles from './CompleteBuilding.module.css';
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import clsx from 'clsx';
import {
  addBuilding,
  checkPayNumber,
  checkResidentExists,
  getBuildingDetails,
  getBuildingDetailsById,
  getDetailsByPayNumber,
  getTenantDetails,
  postAnalyticsEvent,
  updateBuilding,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { rntFieldSchema } from '../../utils/addPropertyScehmas/rntFieldSchema';
import { ArrowRight, ChevronLeft, Sparkles, X } from 'lucide-react';
import { useModal } from '../../hooks/modalContext';
import PropertyManagerData from '../MyHomes/PropertyManagerData';
import { Popover, Whisper } from 'rsuite';
import { BuildingType } from '../../DatePicker/DatePicker';
import { PropertyPrefill } from '../../utils/types';
import { formatApiErrorMessage, getPayNumberInactiveReason } from '../../utils/error';
import { showToast } from '../../utils/showToast';

const initialValuesRNT = {
  rnt: '',
};

const mapPayNumberDetailsToPrefill = (details: {
  rentAmount: number | null;
  address: {
    unitNumber: string | null;
    buildingNumber: string | null;
    streetName: string | null;
    city: string | null;
    province: string | null;
    provinceCode: string | null;
    country: string | null;
    postalCode: string | null;
    fullAddress: string | null;
  };
}): PropertyPrefill => ({
  rent: details.rentAmount,
  address: details.address.fullAddress || '',
  unitNo: details.address.unitNumber || '',
  street: details.address.streetName || '',
  house: details.address.buildingNumber || '',
  city: details.address.city || '',
  province: details.address.province || '',
  provinceCode: details.address.provinceCode || '',
  postal: details.address.postalCode || '',
  country: details.address.country || 'Canada',
});

const CompleteBuilding = () => {
  const { auth, currentUser } = useAuth();
  // A/B test: odd user IDs get Option A (choice screen), even user IDs get Option B (Interac screen)
  const userId = currentUser?.user?.userId;
  const showChoiceScreen = userId != null && userId % 2 === 1;
  const { setModal, modalState } = useModal();
  const payRentSetupFlow = modalState?.payRentSetupFlow === true;
  const isNewBuilding = modalState?.isNewBuilding === true;
  const isFullAddHomeFlow = payRentSetupFlow || isNewBuilding;
  const isChangeRequest = modalState?.isChangeRequest === true;
  const isNewIdvRequired = Boolean(modalState?.isNewIdvRequired);
  const [tenantBuildingId, setTenantBuildingId] = useState(modalState?.tenantBuildingId || '');
  const buildingIdRef = useRef(tenantBuildingId);
  const [isValid, setIsValid] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const latestValidationId = useRef(0);
  const [isCompany, setIsCompany] = useState(false);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [checkingInitialStep, setCheckingInitialStep] = useState(true);
  const [option, setOption] = useState<'rnt' | 'manager' | null>(null);
  const [isFirstProperty, setIsFirstProperty] = useState(true);
  const optionRef = useRef(option);
  optionRef.current = option;

  // Tracked only for the first-time signup flow, not for adding an
  // additional building (isNewBuilding) or editing an existing one.
  const handleBuildingTypeSelection = (selected: 'rnt' | 'manager') => {
    if (payRentSetupFlow) {
      postAnalyticsEvent(
        auth?.data.token,
        'buildingTypeSelection',
        selected === 'rnt' ? 'internal' : 'external'
      );
    }
    setOption(selected);
  };

  const ensureBuildingId = async (rentAmount?: number | null): Promise<string> => {
    if (buildingIdRef.current) {
      return buildingIdRef.current;
    }

    const tenantDetails = await getTenantDetails(auth?.data?.token);
    const accountType = tenantDetails?.data?.tenant?.accountType;
    const buildingType = (accountType === 'both' ? 'personal' : accountType) as BuildingType;

    const addResponse = await addBuilding(auth?.data.token, {
      buildingDetails: {
        unitNumber: '',
        buildingName: '',
        ...(rentAmount != null && rentAmount >= 100 ? { rentAmount } : {}),
        dueDate: 1,
        type: buildingType,
      },
    });

    let buildingId = (
      addResponse as { data?: { tenantBuildingId?: string | number } }
    ).data?.tenantBuildingId?.toString();

    if (!buildingId) {
      const buildingRes = await getBuildingDetails(auth?.data.token);
      const buildings = buildingRes?.data || [];
      buildingId = buildings[buildings.length - 1]?.building?.tenantBuildingId;
    }

    if (!buildingId) {
      throw new Error('Failed to create building');
    }

    buildingIdRef.current = buildingId;
    setTenantBuildingId(buildingId);
    return buildingId;
  };

  const formik = useFormik({
    initialValues: initialValuesRNT,
    validationSchema: rntFieldSchema,
    onSubmit: async (values) => {
      try {
        setLoadingBtn(true);

        const payNumber = values.rnt.toUpperCase();
        let propertyPrefill: PropertyPrefill | undefined;
        try {
          const detailsResponse = await getDetailsByPayNumber(auth?.data.token, payNumber);
          propertyPrefill = mapPayNumberDetailsToPrefill(detailsResponse.data);
        } catch (error) {
          console.error('Error fetching property details by pay number', error);
        }

        // Validate first so a rejected pay number does not leave an empty building behind
        await checkPayNumber(auth?.data.token, payNumber);
        const buildingId = await ensureBuildingId(propertyPrefill?.rent);
        await updateBuilding(auth?.data.token, buildingId, {
          buildingDetails: {
            payNumber,
          },
        });

        setTimeout(() => {
          if (isFullAddHomeFlow) {
            setModal('add-home', {
              tenantBuildingId: buildingId,
              payRentSetupFlow,
              isNewBuilding,
              refreshProperties: true,
              propertyPrefill,
            });
          } else {
            setModal('add-building-address', {
              refreshProfile: true,
              tenantBuildingId: buildingId,
              propertyPrefill,
              isChangeRequest,
              isNewIdvRequired,
            });
          }
          setLoadingBtn(false);
        }, 2000);
      } catch (error) {
        console.error('Error updating building');
        const inactiveReason = getPayNumberInactiveReason(error);
        if (inactiveReason) {
          showToast({ message: formatApiErrorMessage(inactiveReason), type: 'error' });
        }
        setLoadingBtn(false);
      }
    },
  });

  useEffect(() => {
    let cancelled = false;

    const resolveInitialStep = async () => {
      try {
        setCheckingInitialStep(true);

        if (tenantBuildingId) {
          try {
            setFetchingData(true);
            const response = await getBuildingDetailsById(auth?.data.token, tenantBuildingId);
            if (cancelled) {
              return;
            }
            const data = response.data[0];
            if (data?.building?.payNumber) {
              setOption('rnt');
              formik.setFieldValue('rnt', data.building.payNumber);
              validatePayNumber(data.building.payNumber);
              return;
            }
            if (data?.tenantManager?.email) {
              setOption('manager');
              return;
            }
          } catch {
            console.error('Error fetching details of a building');
          } finally {
            if (!cancelled) {
              setFetchingData(false);
            }
          }
        }

        if (optionRef.current) {
          return;
        }

        try {
          const existsResponse = await checkResidentExists(auth?.data.token);
          if (cancelled) {
            return;
          }
          if (existsResponse.data.exists) {
            setOption('rnt');
          }
        } catch {
          console.error('Error checking if resident email exists');
        }
      } finally {
        if (!cancelled) {
          if (!optionRef.current && !showChoiceScreen) {
            handleBuildingTypeSelection('manager');
          }
          setCheckingInitialStep(false);
        }
      }
    };

    resolveInitialStep();
    return () => {
      cancelled = true;
    };
  }, [auth?.data.token, tenantBuildingId, userId]);

  useEffect(() => {
    (async () => {
      try {
        const buildingDetails = await getBuildingDetails(auth?.data.token);
        const buildings = buildingDetails?.data || [];
        setIsFirstProperty(buildings.length === 0);
      } catch (error) {
        console.error('Error checking building count:', error);
      }
    })();
  }, [auth?.data.token]);

  const validatePayNumber = async (payNumber: string) => {
    const normalizedPayNumber = payNumber.toUpperCase();
    const validationId = ++latestValidationId.current;
    setValidationError('');
    if (/^RNT\d{11}$/.test(normalizedPayNumber)) {
      setIsValidating(true);
      try {
        const response = await checkPayNumber(auth?.data.token, normalizedPayNumber);
        if (response.message === 'pay_number_exists') {
          setIsValid(true);
        } else {
          setIsValid(false);
        }
      } catch (error) {
        const inactiveReason = getPayNumberInactiveReason(error);
        if (
          error instanceof Error &&
          (error.message === 'pay_number_not_found' ||
            error.message === 'pay_number_does_not_belong_to_the_company')
        ) {
          setIsValid(false);
          if (error.message === 'pay_number_does_not_belong_to_the_company') {
            setIsCompany(true);
          }
        } else if (inactiveReason) {
          setIsValid(false);
          setValidationError(formatApiErrorMessage(inactiveReason));
        } else {
          console.error('An unexpected error occurred:', (error as Error).message);
          setIsValid(false);
          setValidationError(
            navigator.onLine
              ? 'We could not verify this account number right now. Please try again.'
              : 'You appear to be offline. Please check your internet connection and try again.'
          );
        }
      } finally {
        if (validationId === latestValidationId.current) {
          setIsValidating(false);
        }
      }
    } else {
      setIsValidating(false);
      setIsValid(false);
    }
  };

  const accountNumberHelp = (
    <Popover className='max-w-sm p-5 text-base text-[#4A4F5C] leading-relaxed'>
      <p>
        <span className='font-semibold text-[#0E0E0B] text-lg'>What is a TenantPay account number?</span>
        <br />
        This is the unique payment number assigned to you by your property manager. It always starts
        with <span className='font-semibold'>RNT</span> followed by 11 digits. If your manager is
        registered with TenantPay, they will have already provided this number.
      </p>

      <p className='mt-4'>
        <span className='font-semibold text-[#0E0E0B] text-lg'>Where can I find it?</span>
        <br />
        You can find it in your <span className='font-semibold'>TenantPay Payment Letter</span>{' '}
        shared during onboarding, move-in, or with your rent-payment instructions.
        <br />
        <br />
        You can also find it in the <span className='font-semibold'>Bill Payment section</span> of your bank's app or website. Check your bank's payment setup or bill payment history where TenantPay is listed as a payee.
      </p>

      <p className='mt-4'>
        <span className='font-semibold text-[#0E0E0B] text-lg'>Why do we need it?</span>
        <br />
        If you <span className='font-semibold'>have</span> a TenantPay Account Number but do not
        enter it here, your payment may be{' '}
        <span className='font-semibold'>delayed, rejected, or returned</span>, causing inconvenience
        or possible bank charges. If you don&apos;t have an RNT number, go back and choose
        &quot;Add my rental&quot;.
      </p>
    </Popover>
  );

  const rentDrawingHelp = (
    <Popover className='max-w-sm p-5 text-base text-[#4A4F5C] leading-relaxed'>
      <p>
        <span className='font-semibold text-[#0E0E0B] text-lg'>Monthly rent drop</span>
        <br />
        Complete your setup to join the rent drop. Once you're in, you could win a month\'s rent for free.
      </p>
    </Popover>
  );

  return (
    <>
      <motion.main
        className={clsx(
          styles.main,
          showChoiceScreen && isFirstProperty && option === null && styles.mainWithVideo
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {!isFirstProperty && (
          <X className={styles.crossIcon} size={18} onClick={() => setModal(null)} stroke='#0E0E0B' />
        )}
        {checkingInitialStep && option === null ? (
          <div className={styles.choiceScreen}>
            <div className={styles.stepHeader}>
              <span className={styles.stepIcon}>
                <Sparkles size={20} strokeWidth={2.25} />
              </span>
              <span className={styles.stepLabel}>Get a month's rent covered</span>
            </div>
            <div className={styles.subOuter}>
              <div className={`${styles.loadingField} inputFieldLoading`} />
              <div className={`${styles.loadingField} inputFieldLoading`} />
            </div>
          </div>
        ) : option === null ? (
          <>
            <div className={styles.choiceScreen}>
              <div className={styles.stepHeader}>
                <span className={styles.stepIcon}>
                  <Sparkles size={20} strokeWidth={2.25} />
                </span>
                <span className={styles.stepLabel}>Get a month's rent covered</span>
              </div>

              <div className={styles.subOuter}>
                <h2 className={styles.title}>Enter your rental details</h2>
                <p className={styles.description}>You won&apos;t be charged during setup.</p>
              </div>

              <div className={styles.choiceActions}>
                <button
                  type='button'
                  className={styles.addRentalBtn}
                  onClick={() => handleBuildingTypeSelection('manager')}
                >
                  <span>Add my rental</span>
                  <ArrowRight size={20} stroke='#fff' />
                </button>

                <div className={styles.orDivider}>
                  <span>OR</span>
                </div>

                <div className={styles.accountNumberOuter}>
                  <button
                    type='button'
                    className={styles.addRentalBtn}
                    onClick={() => handleBuildingTypeSelection('rnt')}
                  >
                    <span>I'm already using TenantPay</span>
                    <ArrowRight size={20} stroke='#fff' />
                  </button>
                  <span className={styles.accountNumberSubtitle}>
                    Enter the TenantPay account number (RNT) your landlord shared with you
                  </span>
                </div>
              </div>
            </div>

            {isFirstProperty && (
              <div className={styles.videoContainer}>
                <video className={styles.video} autoPlay muted loop playsInline controls>
                  <source src='https://cdn.tenantpay.com/video/add-building.mp4' type='video/mp4' />
                </video>
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles.upperScroll}>
              <div className={styles.stepHeader}>
                {(option === 'rnt' || showChoiceScreen) && (
                  <button
                    type='button'
                    className={styles.backBtn}
                    onClick={() => setOption(showChoiceScreen ? null : 'manager')}
                    aria-label='Back'
                  >
                    <ChevronLeft size={20} stroke='#0E0E0B' />
                  </button>
                )}
                <span className={styles.stepIcon}>
                  <Sparkles size={20} strokeWidth={2.25} />
                </span>
                <Whisper trigger='click' placement='bottomStart' speaker={rentDrawingHelp}>
                  <button type='button' className={`${styles.stepLabel} cursor-pointer text-left`}>
                    Get a month's rent covered
                  </button>
                </Whisper>
              </div>
              {option === 'manager' && !showChoiceScreen && (
                <div className={styles.subOuter}>
                  <button
                    type='button'
                    className={styles.detailsLink}
                    onClick={() => handleBuildingTypeSelection('rnt')}
                  >
                    Are you already using TenantPay?
                  </button>
                </div>
              )}
              {option === 'rnt' && (
                <div className={styles.subOuter}>
                  <h2 className={styles.title}>Enter your TenantPay account number</h2>
                  <p className={styles.description}>
                    Your TenantPay account number, also called an RNT number, is shared by your
                    landlord. If you have it, enter it here to pay your rent.
                  </p>
                  <Whisper trigger='click' placement='rightStart' speaker={accountNumberHelp}>
                    <button type='button' className={styles.detailsLink}>
                      Not sure? More details
                    </button>
                  </Whisper>
                </div>
              )}
            </div>
            <div className={option === 'manager' ? styles.scrollOuterManager : styles.scrollOuter}>
              {option === 'rnt' ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <form id='complete-building-rnt-form' onSubmit={formik.handleSubmit}>
                    <div className={styles.tenantBox}>
                      <div className={`${styles.upperBox} inputWrapper`}>
                        <div className={`label `}>
                          TenantPay account number<span className={styles.mandatory}>*</span>
                        </div>
                        {fetchingData ? (
                          <div className={'inputFieldLoading'} />
                        ) : (
                          <div style={{ position: 'relative' }}>
                            <span className={styles.rntPrefix}>
                              RNT
                            </span>
                            <input
                              type='text'
                              autoComplete='off'
                              placeholder='00000000000'
                              value={formik.values.rnt.replace(/^RNT/, '')}
                              style={{ paddingLeft: '45px' }}
                              className={clsx(
                                formik.touched.rnt && !isValidating && !isValid ? styles.isInvalid : '',
                                formik.touched.rnt && isValid ? styles.valid : '',
                                `inputField`
                              )}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                }
                              }}
                              onChange={(event) => {
                                let value = event.target.value.toUpperCase();
                                // Remove RNT prefix if user types it
                                value = value.replace(/^RNT/, '');
                                // Account number must be digits only
                                const regex = /^\d*$/;
                                if (regex.test(value)) {
                                  formik.setFieldTouched('rnt', true, true);
                                  formik.setFieldValue('rnt', `RNT${value}`);
                                  validatePayNumber(`RNT${value}`);
                                }
                              }}
                              onPaste={(event) => {
                                event.preventDefault();
                                const pastedText = event.clipboardData.getData('text').toUpperCase();
                                // Remove RNT prefix if it exists in pasted text
                                let cleanedText = pastedText.replace(/^RNT/, '');
                                // Keep only digits after RNT
                                cleanedText = cleanedText.replace(/\D/g, '').substring(0, 11);

                                if (cleanedText) {
                                  formik.setFieldTouched('rnt', true, true);
                                  formik.setFieldValue('rnt', `RNT${cleanedText}`);
                                  validatePayNumber(`RNT${cleanedText}`);
                                }
                              }}
                            />
                          </div>
                        )}
                        {formik.touched.rnt && formik.errors.rnt && (
                          <div className={styles.error}>
                            <span>{formik.errors.rnt}</span>
                          </div>
                        )}

                        {formik.touched.rnt && !isValidating && !isValid && !formik.errors.rnt && (
                          <div className={styles.error}>
                            <span>
                              {validationError ||
                                (!isCompany
                                  ? 'TenantPay account number not found. Please check it and try again.'
                                  : 'TenantPay account number does not belong to the company')}
                            </span>
                          </div>
                        )}
                        <div className={styles.tenantSubhead}>
                          Starts with letters RNT followed by 11 digits
                        </div>
                      </div>
                    </div>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  className='flex flex-col flex-1 min-h-0'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <PropertyManagerData
                    isModal={true}
                    tenantBuildingId={tenantBuildingId}
                    ensureBuildingId={isFullAddHomeFlow ? ensureBuildingId : undefined}
                  />
                </motion.div>
              )}
            </div>
            {option === 'rnt' && (
              <div className={styles.footer}>
                <button
                  className={clsx(
                    styles.nextBtn,
                    Object.keys(formik.errors).length === 0 &&
                      isValid &&
                      !loadingBtn &&
                      !fetchingData &&
                      styles.submitBtn
                  )}
                  disabled={
                    Object.keys(formik.errors).length > 0 || !isValid || loadingBtn || fetchingData
                  }
                  form='complete-building-rnt-form'
                  type='submit'
                >
                  <span>{loadingBtn ? 'Please wait' : 'Continue'}</span>
                  <ArrowRight
                    stroke={
                      Object.keys(formik.errors).length > 0 ||
                      !isValid ||
                      loadingBtn ||
                      fetchingData
                        ? '#0E0E0B66'
                        : '#fff'
                    }
                  />
                </button>
              </div>
            )}
          </>
        )}
      </motion.main>
    </>
  );
};
export default CompleteBuilding;
